<template>
  <Card
    title="Jouw velden"
    subtitle="Extra velden per groep. Die van de leverancier blijven zoals ze zijn, en die van jou blijven staan bij een nieuwe prijslijst."
  >
    <p v-if="error" class="own-fields__error">{{ error }}</p>

    <section v-for="group in sections" :key="group.id" class="own-fields__group">
      <div class="own-fields__group-head">
        <h4>{{ group.name }}</h4>
        <span v-if="group.own" class="own-fields__badge">Eigen groep</span>
        <span v-else class="own-fields__badge own-fields__badge--supplier">Leverancier</span>
        <Btn
          v-if="group.own"
          class="own-fields__remove"
          variant="plain"
          size="sm"
          type="button"
          @click="removeGroup(group)"
        >
          Groep weg
        </Btn>
      </div>

      <ul v-if="fieldsIn(group.id).length" class="own-fields__list">
        <li v-for="option in fieldsIn(group.id)" :key="option.id" class="own-fields__field">
          <span class="own-fields__label">{{ option.label || 'Veld' }}</span>
          <span class="own-fields__type">{{ typeLabel(option.type) }}</span>
          <Btn variant="tertiary" size="sm" type="button" @click="editField(option)">Bewerken</Btn>
          <Btn variant="plain" size="sm" type="button" @click="removeField(option)">Weg</Btn>
        </li>
      </ul>
      <p v-else class="own-fields__empty">Nog geen eigen veld in deze groep.</p>

      <Btn variant="secondary" size="sm" type="button" icon="plus" @click="addField(group.id)">
        Veld in deze groep
      </Btn>
    </section>

    <section v-if="ungrouped.length" class="own-fields__group">
      <div class="own-fields__group-head">
        <h4>Zonder groep</h4>
      </div>
      <ul class="own-fields__list">
        <li v-for="option in ungrouped" :key="option.id" class="own-fields__field">
          <span class="own-fields__label">{{ option.label || 'Veld' }}</span>
          <span class="own-fields__type">{{ typeLabel(option.type) }}</span>
          <Btn variant="tertiary" size="sm" type="button" @click="editField(option)">Bewerken</Btn>
          <Btn variant="plain" size="sm" type="button" @click="removeField(option)">Weg</Btn>
        </li>
      </ul>
    </section>

    <div class="own-fields__actions">
      <Btn variant="tertiary" size="sm" type="button" icon="folder-plus" @click="addGroup">
        Eigen groep
      </Btn>
    </div>
  </Card>
</template>

<script>
  import { computed, onMounted, ref } from 'vue';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';
  import ProductOptionModal from '@/components/products/ProductOptionModal.vue';
  import OptionGroupModal from '@/components/products/OptionGroupModal.vue';
  import { catalogService } from '@/services/catalogService';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';

  const TYPE_LABELS = {
    number: 'Getal',
    'single-choice': 'Keuze',
    'multi-choice': 'Meerdere keuzes',
    toggle: 'Schakelaar',
    text: 'Tekst',
    notice: 'Tekstblok',
    image: 'Afbeelding',
    photo: 'Foto',
  };

  export default {
    name: 'CatalogOwnFieldsCard',
    components: { Card, Btn },
    props: {
      installationId: { type: String, required: true },
      groups: { type: Array, default: () => [] },
      options: { type: Array, default: () => [] },
    },
    emits: ['saved'],
    setup(props, { emit }) {
      const toastStore = useToastStore();
      const uiStore = useUIStore();
      const ownOptions = ref([]);
      const ownGroups = ref([]);
      const groupsColumn = ref(false);
      const error = ref('');

      const supplierGroups = computed(() => {
        const ownIds = new Set(ownGroups.value.map((group) => group.id));
        return (props.groups || []).filter(
          (group) => group?.id && !ownIds.has(group.id) && group.catalog_origin !== 'dealer'
        );
      });

      const sections = computed(() => [
        ...supplierGroups.value.map((group) => ({ ...group, own: false })),
        ...ownGroups.value.map((group) => ({ ...group, own: true })),
      ]);

      const fieldsIn = (groupId) =>
        ownOptions.value.filter((option) => (option.pageGroupId || null) === groupId);

      const ungrouped = computed(() =>
        ownOptions.value.filter((option) => !option.pageGroupId && !option.page_group_id)
      );

      const typeLabel = (type) => TYPE_LABELS[type] || type || 'Veld';

      const allGroups = () => [...supplierGroups.value, ...ownGroups.value];

      const load = async () => {
        error.value = '';
        try {
          const result = await catalogService.getInstallation(props.installationId);
          const installation = result.installation || {};
          ownOptions.value = Array.isArray(installation.own_options)
            ? installation.own_options.map((option) => ({ ...option }))
            : [];
          groupsColumn.value = Array.isArray(installation.own_option_groups);
          ownGroups.value = groupsColumn.value
            ? installation.own_option_groups.map((group) => ({ ...group }))
            : [];
        } catch (err) {
          error.value = err?.response?.data?.error || 'Eigen velden laden mislukt';
        }
      };

      const persist = async () => {
        const body = {
          own_options: JSON.parse(JSON.stringify(ownOptions.value)),
        };
        if (groupsColumn.value || ownGroups.value.length) {
          body.own_option_groups = JSON.parse(JSON.stringify(ownGroups.value));
        }
        try {
          await catalogService.updateInstallation(props.installationId, body);
          groupsColumn.value = groupsColumn.value || ownGroups.value.length > 0;
          toastStore.addToast({ type: 'success', message: 'Eigen velden opgeslagen' });
          emit('saved');
        } catch (err) {
          const raw = err?.response?.data?.error || err?.response?.data?.message || '';
          const missingColumn = String(raw).includes('own_option_groups');
          toastStore.addToast({
            type: 'error',
            message: missingColumn
              ? 'Eigen groepen opslaan lukt nog niet. Voer eerst de migratie voor own_option_groups uit.'
              : raw || 'Eigen velden opslaan mislukt',
          });
          await load();
        }
      };

      const addField = (groupId) => {
        const modalId = uiStore.addModal(ProductOptionModal, {
          title: 'Eigen veld',
          size: 'xl',
          initialGroupId: groupId || '',
          optionGroups: allGroups(),
          availableOptions: props.options || [],
          onSubmit: async (option) => {
            ownOptions.value = [
              ...ownOptions.value.filter((item) => item.id !== option.id),
              { ...option, catalog_origin: 'dealer' },
            ];
            uiStore.closeModal(modalId);
            await persist();
          },
          onCreateGroup: async (groupData) => {
            const id = `own_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
            ownGroups.value = [...ownGroups.value, { id, name: groupData.name }];
            uiStore.updateModalProps(modalId, { optionGroups: allGroups() });
            await persist();
          },
        });
      };

      const editField = (option) => {
        const modalId = uiStore.addModal(ProductOptionModal, {
          title: 'Eigen veld',
          size: 'xl',
          option: JSON.parse(JSON.stringify(option)),
          optionGroups: allGroups(),
          availableOptions: props.options || [],
          onSubmit: async (next) => {
            ownOptions.value = ownOptions.value.map((item) =>
              item.id === option.id ? { ...next, id: option.id, catalog_origin: 'dealer' } : item
            );
            uiStore.closeModal(modalId);
            await persist();
          },
        });
      };

      const removeField = (option) => {
        const modalId = uiStore.addModal(false, {
          title: 'Veld verwijderen?',
          description: `«${option.label || 'Dit veld'}» verdwijnt uit het formulier.`,
          submitText: 'Verwijderen',
          cancelText: 'Annuleren',
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            ownOptions.value = ownOptions.value.filter((item) => item.id !== option.id);
            await persist();
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const addGroup = () => {
        const modalId = uiStore.addModal(OptionGroupModal, {
          title: 'Eigen groep',
          onSubmit: async (groupData) => {
            const id = `own_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
            ownGroups.value = [...ownGroups.value, { id, name: groupData.name }];
            uiStore.closeModal(modalId);
            await persist();
          },
        });
      };

      const removeGroup = (group) => {
        const modalId = uiStore.addModal(false, {
          title: 'Groep verwijderen?',
          description: 'De velden in deze groep blijven bestaan, zonder groep.',
          submitText: 'Verwijderen',
          cancelText: 'Annuleren',
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            ownGroups.value = ownGroups.value.filter((item) => item.id !== group.id);
            ownOptions.value = ownOptions.value.map((option) =>
              option.pageGroupId === group.id ? { ...option, pageGroupId: null } : option
            );
            await persist();
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      onMounted(load);

      return {
        error,
        sections,
        ungrouped,
        fieldsIn,
        typeLabel,
        addField,
        editField,
        removeField,
        addGroup,
        removeGroup,
      };
    },
  };
</script>

<style scoped lang="scss">
  .own-fields__error {
    margin: 0 0 1rem;
    color: var(--red-600);
    font-size: 0.875rem;
  }

  .own-fields__group {
    padding: 0.875rem 0;
    border-top: 1px solid var(--slate-200);

    &:first-of-type {
      border-top: 0;
      padding-top: 0;
    }
  }

  .own-fields__group-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.625rem;

    h4 {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
    }
  }

  .own-fields__badge {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--brand-700);
    background: var(--brand-50);
    border-radius: 999px;
    padding: 0.125rem 0.5rem;

    &--supplier {
      color: var(--slate-600);
      background: var(--slate-100);
    }
  }

  .own-fields__remove {
    margin-left: auto;
  }

  .own-fields__list {
    list-style: none;
    margin: 0 0 0.75rem;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .own-fields__field {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .own-fields__label {
    font-size: 0.875rem;
    color: var(--slate-800);
  }

  .own-fields__type {
    font-size: 0.75rem;
    color: var(--slate-500);
    margin-right: auto;
  }

  .own-fields__empty {
    margin: 0 0 0.75rem;
    font-size: 0.8125rem;
    color: var(--slate-500);
  }

  .own-fields__actions {
    margin-top: 0.5rem;
  }
</style>
